import {
  Ban,
  History,
  Lock,
  Pencil,
  TriangleAlert,
  Trash2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useHasPermission } from "@/hooks/use-auth";

import {
  RIDER_SALE_PAYMENT_METHOD_LABELS,
  RIDER_SALE_STATUS_BADGE_VARIANT,
  RIDER_SALE_STATUS_LABELS,
} from "./rider-sale-labels";

import type { RiderSale } from "@artisancode/api-types";

interface RiderSaleMobileCardProps {
  sale: RiderSale;
  canUpdate: boolean;
  canDelete: boolean;
  canVoid: boolean;
  onEdit: (sale: RiderSale) => void;
  onDelete: (sale: RiderSale) => void;
  onVoid: (sale: RiderSale) => void;
  onShowDetail: (sale: RiderSale) => void;
  onHistory?: (sale: RiderSale) => void;
}

export function RiderSaleMobileCard({
  sale: r,
  canUpdate,
  canDelete,
  canVoid,
  onEdit,
  onDelete,
  onVoid,
  onShowDetail,
  onHistory,
}: RiderSaleMobileCardProps) {
  const canViewLogs = useHasPermission("rider_sales.view");
  const isDraft = r.status === "draft";
  const selisih = (r.totalRevenue ?? 0) - r.totalCollected;
  return (
    <Card className="overflow-hidden border border-border shadow-xs">
      <div className="p-3.5 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-sm">{r.date}</span>
            {r.transactionCode && (
              <Badge
                variant="outline"
                className="font-mono text-[10px] px-1.5 py-0 bg-muted/40 text-foreground"
              >
                {r.transactionCode}
              </Badge>
            )}
          </div>
          <Badge
            variant={RIDER_SALE_STATUS_BADGE_VARIANT[r.status]}
            className="text-[11px] px-2 py-0.5"
          >
            {RIDER_SALE_STATUS_LABELS[r.status]}
          </Badge>
        </div>
        {r.status === "cancelled" && r.cancelReason && (
          <p className="text-xs text-muted-foreground italic">
            &ldquo;{r.cancelReason}&rdquo;
          </p>
        )}

        <div>
          <p className="font-semibold text-sm text-foreground">{r.riderName}</p>
        </div>

        {(() => {
          const totalReturned = r.lines.reduce(
            (s, l) => s + (l.returnedQty ?? 0),
            0,
          );
          const totalDiscrepancy = r.lines.reduce(
            (s, l) => s + (l.discrepancyQty ?? 0),
            0,
          );
          return (
            <div className="grid grid-cols-4 gap-1.5 rounded-lg bg-muted/40 p-2 text-center text-xs">
              <div>
                <span className="text-[10px] text-muted-foreground block">
                  Ambil
                </span>
                <span className="font-semibold text-foreground text-sm">
                  {r.totalDispatchedQty}
                </span>
              </div>
              <div className="border-l border-border/50">
                <span className="text-[10px] text-muted-foreground block">
                  Terjual
                </span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm">
                  {r.totalSoldQty}
                </span>
              </div>
              <div className="border-l border-border/50">
                <span className="text-[10px] text-muted-foreground block">
                  Kembali
                </span>
                <span className="font-semibold text-foreground text-sm">
                  {totalReturned}
                </span>
              </div>
              <div className="border-l border-border/50">
                <span className="text-[10px] text-muted-foreground block">
                  Selisih
                </span>
                <span
                  className={`font-semibold text-sm ${
                    totalDiscrepancy > 0
                      ? "text-destructive font-bold"
                      : "text-muted-foreground"
                  }`}
                >
                  {totalDiscrepancy}
                </span>
              </div>
            </div>
          );
        })()}

        <div className="flex justify-end">
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs text-primary"
            onClick={() => onShowDetail(r)}
          >
            Detail penjualan →
          </Button>
        </div>

        <div className="space-y-1.5 rounded-md border border-border/50 p-2.5 text-xs bg-muted/20">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Total Setoran:</span>
            <span className="font-bold text-foreground">
              Rp {(r.totalRevenue ?? 0).toLocaleString("id-ID")}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Bonus Rider:</span>
            <span className="font-medium text-foreground">
              Rp {(r.totalBonus ?? 0).toLocaleString("id-ID")}
            </span>
          </div>
          <div className="flex justify-between items-start pt-1.5 border-t border-border/40">
            <span className="text-muted-foreground shrink-0">Pembayaran:</span>
            <div className="text-right">
              {r.payments.length === 0 ? (
                <span className="text-muted-foreground">Belum ada</span>
              ) : (
                r.payments.map((p) => (
                  <div key={p.method} className="font-medium">
                    {RIDER_SALE_PAYMENT_METHOD_LABELS[p.method]}: Rp{" "}
                    {p.amount.toLocaleString("id-ID")}
                  </div>
                ))
              )}
              {selisih !== 0 && (
                <div className="flex items-center justify-end gap-1 text-[11px] text-amber-600 font-medium mt-0.5">
                  <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    Selisih Rp {Math.abs(selisih).toLocaleString("id-ID")}{" "}
                    {selisih > 0 ? "kurang" : "lebih"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-1 pt-1 border-t border-border/40 flex-wrap">
          {canViewLogs && onHistory && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => onHistory(r)}
              title="Riwayat Aktivitas"
              aria-label="Riwayat Aktivitas"
            >
              <History className="mr-1 h-3.5 w-3.5" />
              Riwayat Aktivitas
            </Button>
          )}
          {r.locked ? (
            <div
              className="flex items-center gap-1 text-xs text-muted-foreground"
              title="Sudah dipilih ke Penggajian Rider — batalkan payroll-nya dulu untuk mengubah/menghapus"
            >
              <Lock className="h-3.5 w-3.5" />
              <span>Dari Payroll</span>
            </div>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2.5 text-xs"
                onClick={() => onEdit(r)}
                disabled={!canUpdate || !isDraft}
              >
                <Pencil className="mr-1 h-3.5 w-3.5" />
                Edit
              </Button>
              {isDraft ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => onDelete(r)}
                  disabled={!canDelete}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              ) : (
                r.status !== "cancelled" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => onVoid(r)}
                    disabled={!canVoid}
                  >
                    <Ban className="h-3.5 w-3.5" />
                  </Button>
                )
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
